import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  Dimensions,
  ActivityIndicator,
  TouchableOpacity,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import { useUserPreferences } from '../context/UserPreferencesContext';
import ProfileIcon from '../components/ProfileIcon';
import TrainingSessionTimelineView from '../components/TrainingSessionTimelineView';
import TrainingCalendarDayDots from '../components/TrainingCalendarDayDots';
import TrainingCalendarExerciseFilters from '../components/TrainingCalendarExerciseFilters';
import { getApiUrl } from '../utils/apiConfig';
import { isoDateToDisplay } from '../utils/dateFormat';
import {
  applyCalendarFilters,
  createEmptyCalendarFilters,
  parseAssignedCourses,
} from '../utils/trainingCalendarFilters';

const CHART_COLORS = ['#2e844a', '#d4af37', '#0176d3', '#5c4ee5', '#e07f13', '#032d60', '#ba0517', '#706e6b'];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function useWindowWidth() {
  const [w, setW] = useState(Dimensions.get('window').width);
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setW(window.width));
    return () => sub?.remove?.();
  }, []);
  return w;
}

function buildMonthCells(year, monthIndex) {
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function isoFromParts(year, monthIndex, day) {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function HorizontalBars({ rows, labelKey, theme, isDark, valueKey = 'session_count' }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <View style={styles.barBlock}>
      {rows.length === 0 ? (
        <Text style={[styles.emptyHint, { color: theme.colors.text }]}>No data for this period.</Text>
      ) : (
        rows.map((row, i) => {
          const pct = ((row[valueKey] || 0) / max) * 100;
          const label = row[labelKey] || '—';
          return (
            <View key={`${label}-${i}`} style={styles.hBarRow}>
              <Text style={[styles.hBarLabel, { color: theme.colors.text }]} numberOfLines={2}>
                {label}
              </Text>
              <View style={[styles.hBarTrack, { backgroundColor: isDark ? '#333' : '#e5e5e5' }]}>
                <View
                  style={[
                    styles.hBarFill,
                    {
                      width: `${pct}%`,
                      backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                    },
                  ]}
                />
              </View>
              <Text style={[styles.hBarCount, { color: theme.colors.heading }]}>{row[valueKey]}</Text>
            </View>
          );
        })
      )}
    </View>
  );
}

function StatMetric({ value, label, color, theme }) {
  return (
    <View style={styles.metricBig}>
      <Text style={[styles.metricNumber, { color }]}>{value}</Text>
      <Text style={[styles.metricCaption, { color: theme.colors.text }]}>{label}</Text>
    </View>
  );
}


export default function MyCalendarPage({ navigation, route }) {
  const theme = useTheme();
  const { isUSUser } = useUserPreferences();
  const windowWidth = useWindowWidth();
  const { width: dimWidth } = useWindowDimensions();
  const gold = theme.colors.primary || '#d4af37';
  const isDark = theme.isDarkMode;

  const today = useMemo(() => new Date(), []);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonthIndex, setViewMonthIndex] = useState(today.getMonth());
  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState(null);
  const [dayModalVisible, setDayModalVisible] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [selectedDaySessions, setSelectedDaySessions] = useState([]);
  const [calendarFilters, setCalendarFilters] = useState(createEmptyCalendarFilters);

  const assignedCourses = useMemo(
    () => parseAssignedCourses(currentUser?.courses),
    [currentUser?.courses]
  );

  const pageBg = isDark ? theme.colors.background : '#f3f2f2';
  const calendarCream = '#faf8f4';
  const cardBg = isDark ? theme.colors.surface : calendarCream;
  const cardBorder = isDark ? '#3e3e3c' : '#dddbda';
  const bannerBg = isDark ? '#032d60' : '#5c4ee5';

  const gridItemWidth =
    windowWidth >= 900 ? '31%' : windowWidth >= 560 ? '47%' : '100%';

  const fetchUser = useCallback(async (userId) => {
    try {
      const res = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (res.ok) setCurrentUser(await res.json());
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const userId = route?.params?.userId || route?.params?.user?.id;
    if (route?.params?.user?.id) setCurrentUser(route.params.user);
    if (userId) void fetchUser(userId);
  }, [route?.params?.userId, route?.params?.user, fetchUser]);

  const studentId = currentUser?.id;

  const loadCalendar = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/students/${encodeURIComponent(String(studentId))}/training-calendar?year=${viewYear}&month=${viewMonthIndex + 1}`
      );
      if (res.ok) {
        setCalendarData(await res.json());
      } else {
        setCalendarData(null);
      }
    } catch {
      setCalendarData(null);
    } finally {
      setLoading(false);
    }
  }, [studentId, viewYear, viewMonthIndex]);

  useEffect(() => {
    if (studentId) void loadCalendar();
  }, [studentId, loadCalendar]);

  const sessionCountsByDate = calendarData?.session_counts_by_date || {};
  const courseTypesByDate = calendarData?.course_types_by_date || {};
  const coursesByDate = calendarData?.courses_by_date || {};
  const filteredCalendar = useMemo(
    () =>
      applyCalendarFilters({
        sessionCountsByDate,
        courseTypesByDate,
        coursesByDate,
        filters: calendarFilters,
      }),
    [sessionCountsByDate, courseTypesByDate, coursesByDate, calendarFilters]
  );
  const displaySessionCountsByDate = filteredCalendar.sessionCountsByDate;
  const displayCourseTypesByDate = filteredCalendar.courseTypesByDate;
  const stats = calendarData?.stats || {};
  const cells = useMemo(
    () => buildMonthCells(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex]
  );

  const calendarWidth = Math.min(560, dimWidth - 40);
  const dayCellSize = Math.floor((calendarWidth - 16) / 7);

  const goPrevMonth = () => {
    if (viewMonthIndex === 0) {
      setViewMonthIndex(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonthIndex((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonthIndex === 11) {
      setViewMonthIndex(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonthIndex((m) => m + 1);
    }
  };

  const closeDayModal = useCallback(() => {
    setDayModalVisible(false);
    setSelectedDateKey(null);
    setSelectedDaySessions([]);
  }, []);

  const openDayModal = (dateKey) => {
    let sessions = calendarData?.sessions_by_date?.[dateKey] || [];
    if (sessions.length === 0 && Array.isArray(calendarData?.sessions)) {
      sessions = calendarData.sessions.filter(
        (s) => String(s.session_date || '').slice(0, 10) === dateKey
      );
    }
    if (sessions.length === 0) return;
    setSelectedDateKey(dateKey);
    setSelectedDaySessions(sessions);
    setDayModalVisible(true);
  };

  const goBackToSessionBuilder = () => {
    navigation.navigate('SessionBuilder', {
      userId: currentUser?.id || route?.params?.userId,
      user: currentUser || route?.params?.user,
    });
  };

  const renderWidget = (title, subtitle, children) => (
    <View
      style={[
        styles.widgetCard,
        {
          width: gridItemWidth,
          backgroundColor: cardBg,
          borderColor: cardBorder,
        },
      ]}
    >
      <View style={[styles.widgetHead, { borderBottomColor: cardBorder }]}>
        <Text style={[styles.widgetTitle, { color: theme.colors.heading }]}>{title}</Text>
        {subtitle ? (
          <Text style={[styles.widgetSub, { color: theme.colors.text }]}>{subtitle}</Text>
        ) : null}
      </View>
      {children}
    </View>
  );

  const todayIso = isoFromParts(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <View
      style={[styles.container, { backgroundColor: pageBg }]}
      data-screen="my-calendar"
      data-theme={isDark ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { myCalendarScreen: 'true' } } : {})}
    >
      <StatusBar style={isDark ? 'light' : 'dark'} />

      <View
        style={[styles.header, { borderBottomColor: cardBorder, backgroundColor: pageBg }]}
        data-header="true"
      >
        <TouchableOpacity
          style={styles.backBtn}
          onPress={goBackToSessionBuilder}
          accessibilityRole="button"
          accessibilityLabel="Back to Session Builder"
        >
          <MaterialIcons name="arrow-back" size={24} color={theme.colors.heading} />
        </TouchableOpacity>
        <View style={styles.breadcrumbContainer}>
          <Text style={[styles.breadcrumb, { color: theme.colors.heading }]}>
            My Training Calendar
          </Text>
        </View>
        <ProfileIcon navigation={navigation} user={currentUser} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        <View style={[styles.sfBanner, { backgroundColor: bannerBg }]}>
          <Text style={styles.sfBannerTitle}>Training calendar</Text>
          <Text style={styles.sfBannerSub}>
            Yellow days have logged sessions; coloured dots match course types trained that day.
          </Text>
        </View>

        <TrainingCalendarExerciseFilters
          assignedCourses={assignedCourses}
          filters={calendarFilters}
          onFiltersChange={setCalendarFilters}
          onRefresh={() => setCalendarFilters(createEmptyCalendarFilters())}
        />

        <View
          style={[
            styles.calendarCard,
            { backgroundColor: cardBg, borderColor: cardBorder },
          ]}
          {...(Platform.OS === 'web' ? { dataSet: { myCalendarGrid: 'true' } } : {})}
        >
          <View style={styles.calendarNavRow}>
            <TouchableOpacity onPress={goPrevMonth} style={styles.monthNavBtn} accessibilityLabel="Previous month">
              <MaterialIcons name="chevron-left" size={28} color={gold} />
            </TouchableOpacity>
            <Text style={[styles.monthTitle, { color: theme.colors.heading }]}>
              {MONTH_NAMES[viewMonthIndex]} {viewYear}
            </Text>
            <TouchableOpacity onPress={goNextMonth} style={styles.monthNavBtn} accessibilityLabel="Next month">
              <MaterialIcons name="chevron-right" size={28} color={gold} />
            </TouchableOpacity>
          </View>

          <View style={[styles.weekdayRow, { width: calendarWidth }]}>
            {WEEKDAY_LABELS.map((label) => (
              <Text
                key={label}
                style={[styles.weekdayLabel, { width: dayCellSize, color: theme.colors.text }]}
              >
                {label}
              </Text>
            ))}
          </View>

          {loading ? (
            <ActivityIndicator size="large" color={gold} style={{ marginVertical: 32 }} />
          ) : (
            <View style={[styles.daysGrid, { width: calendarWidth }]}>
              {cells.map((day, idx) => {
                if (day == null) {
                  return (
                    <View
                      key={`empty-${idx}`}
                      style={{
                        width: dayCellSize,
                        height: dayCellSize,
                        backgroundColor: isDark ? 'transparent' : calendarCream,
                      }}
                    />
                  );
                }
                const dateKey = isoFromParts(viewYear, viewMonthIndex, day);
                const sessionCount = displaySessionCountsByDate[dateKey] || 0;
                const dayCourseTypes = displayCourseTypesByDate[dateKey] || [];
                const isToday = dateKey === todayIso;
                const hasSessions = sessionCount > 0;

                return (
                  <TouchableOpacity
                    key={dateKey}
                    style={[
                      styles.dayCell,
                      {
                        width: dayCellSize,
                        height: dayCellSize,
                        borderColor: isToday ? gold : 'transparent',
                        backgroundColor: hasSessions
                          ? isDark
                            ? 'rgba(253, 253, 10, 0.18)'
                            : '#f9ffd6'
                          : isDark
                            ? 'transparent'
                            : calendarCream,
                      },
                    ]}
                    onPress={() => hasSessions && openDayModal(dateKey)}
                    disabled={!hasSessions}
                    activeOpacity={hasSessions ? 0.7 : 1}
                    {...(Platform.OS === 'web'
                      ? {
                          dataSet: {
                            myCalendarDay: 'true',
                            ...(hasSessions ? { myCalendarDayHasSessions: 'true' } : {}),
                            ...(isToday ? { myCalendarDayToday: 'true' } : {}),
                          },
                        }
                      : {})}
                    accessibilityLabel={
                      hasSessions
                        ? `${day} ${MONTH_NAMES[viewMonthIndex]}, ${sessionCount} session(s)`
                        : `${day} ${MONTH_NAMES[viewMonthIndex]}`
                    }
                  >
                    <Text
                      style={[
                        styles.dayNumber,
                        {
                          color: isToday && isDark
                            ? '#ffffff'
                            : hasSessions
                              ? theme.colors.heading
                              : theme.colors.text,
                          fontWeight: hasSessions || isToday ? '700' : '500',
                        },
                      ]}
                    >
                      {day}
                    </Text>
                    {hasSessions ? (
                      <TrainingCalendarDayDots
                        courseTypes={dayCourseTypes}
                        fallbackSessionCount={sessionCount}
                      />
                    ) : null}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>

        <View style={[styles.sfBanner, { backgroundColor: isDark ? '#1e1e1e' : gold, marginTop: 8 }]}>
          <Text style={styles.sfBannerTitle}>Training report</Text>
          <Text style={styles.sfBannerSub}>Your activity summary for this month and week</Text>
        </View>

        <View style={styles.gridRow}>
          {renderWidget(
            'Sessions this month',
            `${MONTH_NAMES[viewMonthIndex]} ${viewYear}`,
            <StatMetric
              value={stats.trained_this_month ?? 0}
              label="How many times have I trained this month"
              color="#2e844a"
              theme={theme}
            />
          )}
          {renderWidget(
            'Sessions this week',
            'Current calendar week',
            <StatMetric
              value={stats.trained_this_week ?? 0}
              label="How many times have I trained this week"
              color="#0176d3"
              theme={theme}
            />
          )}
          {renderWidget(
            'Courses this month',
            'Distinct courses trained',
            <StatMetric
              value={stats.distinct_courses_this_month ?? 0}
              label="How many courses have I trained in this month"
              color={gold}
              theme={theme}
            />
          )}
        </View>

        <View style={styles.gridRow}>
          {renderWidget(
            'Average courses per week',
            'Distinct courses per session (this week)',
            <StatMetric
              value={Number(stats.avg_courses_per_week ?? 0).toFixed(1)}
              label="On average, courses per session this week"
              color="#5c4ee5"
              theme={theme}
            />
          )}
          {renderWidget(
            'Average courses per month',
            `Distinct courses per session (${MONTH_NAMES[viewMonthIndex]})`,
            <StatMetric
              value={Number(stats.avg_courses_per_month ?? 0).toFixed(1)}
              label="On average, courses per session this month"
              color="#e07f13"
              theme={theme}
            />
          )}
          {renderWidget(
            'Courses trained this month',
            'Sessions per course',
            <HorizontalBars
              rows={stats.courses_this_month || []}
              labelKey="course_label"
              theme={theme}
              isDark={isDark}
            />
          )}
        </View>

        <View style={styles.gridRow}>
          {renderWidget(
            'Courses trained this week',
            'Sessions per course',
            <HorizontalBars
              rows={stats.courses_this_week || []}
              labelKey="course_label"
              theme={theme}
              isDark={isDark}
            />
          )}
        </View>
      </ScrollView>

      {dayModalVisible ? (
        <View
          style={styles.dayModalOverlay}
          {...(Platform.OS === 'web'
            ? { dataSet: { myCalendarDayOverlay: 'true' } }
            : {})}
        >
          <Pressable
            style={styles.dayModalBackdrop}
            onPress={closeDayModal}
            accessibilityRole="button"
            accessibilityLabel="Close session details"
          />
          <View
            style={[
              styles.dayModalSheet,
              {
                backgroundColor: cardBg,
                borderColor: cardBorder,
              },
            ]}
            {...(Platform.OS === 'web'
              ? { dataSet: { myCalendarDayModalSheet: 'true' } }
              : {})}
            onStartShouldSetResponder={() => true}
          >
            <View style={[styles.modalHeader, { borderBottomColor: cardBorder }]}>
              <Text style={[styles.modalTitle, { color: theme.colors.heading }]}>
                {selectedDateKey
                  ? isoDateToDisplay(selectedDateKey, isUSUser)
                  : 'Training sessions'}
              </Text>
              <TouchableOpacity onPress={closeDayModal} accessibilityLabel="Close">
                <MaterialIcons name="close" size={26} color={theme.colors.heading} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.modalScroll}
              contentContainerStyle={styles.modalScrollContent}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              {selectedDaySessions.map((session, si) => (
                <View key={session.id || si} style={styles.sessionBlock}>
                  <Text style={[styles.sessionBlockTitle, { color: theme.colors.heading }]}>
                    Session {session.session_number != null ? `#${session.session_number}` : si + 1}
                  </Text>
                  <TrainingSessionTimelineView
                    trainingSessionId={session.id}
                    studentId={studentId}
                    sessionNotes={session.notes}
                    durationMinutes={session.duration_minutes}
                    readOnly
                    initialExpanded
                  />
                </View>
              ))}
            </ScrollView>
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, position: 'relative' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: Platform.OS === 'web' ? 16 : 48,
    paddingBottom: 14,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backBtn: {
    padding: 6,
    marginRight: 4,
  },
  breadcrumbContainer: { flex: 1 },
  breadcrumb: { fontSize: 18, fontWeight: '700' },
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 48, paddingHorizontal: 8 },
  sfBanner: {
    marginHorizontal: 8,
    marginTop: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 4,
  },
  sfBannerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  sfBannerSub: { color: 'rgba(255,255,255,0.9)', fontSize: 12, marginTop: 4 },
  calendarCard: {
    marginHorizontal: 8,
    marginTop: 12,
    padding: 16,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.08,
          shadowRadius: 2,
          elevation: 2,
        }),
  },
  calendarNavRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 12,
  },
  monthNavBtn: { padding: 4 },
  monthTitle: { fontSize: 18, fontWeight: '700' },
  weekdayRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  weekdayLabel: {
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center',
    opacity: 0.75,
  },
  daysGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dayCell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: 8,
    borderWidth: 2,
    marginBottom: 2,
    paddingTop: 4,
    paddingBottom: 2,
  },
  dayNumber: { fontSize: 14 },
  gridRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    paddingHorizontal: 4,
  },
  widgetCard: {
    borderRadius: 4,
    borderWidth: 1,
    padding: 12,
    minHeight: 160,
    margin: 4,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.08,
          shadowRadius: 2,
          elevation: 2,
        }),
  },
  widgetHead: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  widgetTitle: { fontSize: 15, fontWeight: '700' },
  widgetSub: { fontSize: 11, marginTop: 4, opacity: 0.8 },
  metricBig: { alignItems: 'center', paddingVertical: 16 },
  metricNumber: { fontSize: 44, fontWeight: '800' },
  metricCaption: { fontSize: 12, marginTop: 8, textAlign: 'center', lineHeight: 17 },
  barBlock: { gap: 8 },
  hBarRow: { marginBottom: 10 },
  hBarLabel: { fontSize: 12, marginBottom: 4, fontWeight: '600' },
  hBarTrack: { height: 10, borderRadius: 4, overflow: 'hidden' },
  hBarFill: { height: '100%', borderRadius: 4 },
  hBarCount: { fontSize: 11, marginTop: 4, fontWeight: '700' },
  emptyHint: { fontSize: 13, fontStyle: 'italic', paddingVertical: 8 },
  dayModalOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
    elevation: 1000,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  dayModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  dayModalSheet: {
    width: '100%',
    maxWidth: 720,
    maxHeight: '85%',
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    zIndex: 1,
    ...(Platform.OS === 'web' ? { boxShadow: '0 8px 32px rgba(0,0,0,0.35)' } : {}),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  modalTitle: { fontSize: 18, fontWeight: '700', flex: 1, marginRight: 12 },
  modalScroll: {
    flexGrow: 0,
    flexShrink: 1,
    maxHeight: Platform.OS === 'web' ? 520 : 440,
  },
  modalScrollContent: { padding: 16, paddingBottom: 32 },
  sessionBlock: { marginBottom: 24 },
  sessionBlockTitle: { fontSize: 16, fontWeight: '700', marginBottom: 6 },
  sessionMeta: { fontSize: 13, marginBottom: 4, opacity: 0.85 },
  sessionNotes: { fontSize: 13, marginBottom: 10, lineHeight: 18, fontStyle: 'italic' },
  timelineErrorText: {
    fontSize: 14,
    fontStyle: 'italic',
    opacity: 0.85,
    paddingVertical: 8,
  },
});
